import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Landmark, Tent, ShoppingBag, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ORIGINS, INTERESTS, originName, travelStyle } from "@/lib/tripProfile";
import { trackEvent } from "@/lib/analytics";
import LocateButton from "@/components/LocateButton";

const ICON = { culture: Landmark, adventure: Tent, shopping: ShoppingBag };
const slide = { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.22 } };

function Step({ n, total, title, children }) {
  const { t } = useLang();
  return (
    <motion.div key={n} {...slide} className="space-y-4" data-testid={`onboarding-step-${n}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("ob_step").replace("{n}", n).replace("{total}", total)}</div>
      <h2 className="font-display font-bold text-2xl sm:text-3xl text-ink leading-tight">{title}</h2>
      {children}
    </motion.div>
  );
}

function DestGrid({ countries, lang, value, onPick }) {
  const list = useMemo(() => [...countries].sort((a, b) => (a.short_names[lang] || "").localeCompare(b.short_names[lang] || "", lang)), [countries, lang]);
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[46vh] overflow-y-auto pe-1" data-testid="ob-dest-grid">
      {list.map((c) => (
        <button key={c.code} type="button" onClick={() => onPick(c.code)} data-testid={`ob-dest-${c.code}`}
          className={`rounded-xl border p-2 text-start flex items-center gap-2 transition-colors ${value === c.code ? "border-ochre bg-ochre/10" : "border-stoneLine hover:border-ochre/50"}`}>
          <img src={c.flag_url} alt="" className="w-7 h-5 object-cover rounded-sm shrink-0" />
          <span className="text-xs font-semibold text-ink leading-tight">{c.short_names[lang]}</span>
        </button>
      ))}
    </div>
  );
}

/** 5-step wizard: name → origin → destination → 3/5 days → interests. Returns a profile object. */
export default function Onboarding({ countries, initial, onDone, onCancel }) {
  const { lang, t } = useLang();
  const [step, setStep] = useState(1);
  const [p, setP] = useState({ name: "", origin: "IT", dest: null, days: 3, interests: ["culture"], ...initial });
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const can = step === 1 ? p.name.trim().length >= 2 : step === 3 ? Boolean(p.dest) : step === 5 ? p.interests.length > 0 : true;
  const origins = useMemo(() => ORIGINS.map((o) => ({ ...o, label: originName(o.code, lang) })).sort((a, b) => a.label.localeCompare(b.label, lang)), [lang]);
  const next = () => {
    if (step < 5) return setStep(step + 1);
    const profile = { ...p, name: p.name.trim(), style: travelStyle(p.interests), createdAt: Date.now() };
    trackEvent("trip_onboarding_done", { dest: p.dest, days: p.days, interests: p.interests.join(",") });
    onDone(profile);
  };
  const toggleInterest = (i) => set("interests", p.interests.includes(i) ? p.interests.filter((x) => x !== i) : [...p.interests, i]);
  return (
    <div className="max-w-xl mx-auto rounded-3xl border border-stoneLine bg-card shadow-xl p-5 sm:p-8" data-testid="trip-onboarding">
      <div className="flex gap-1 mb-5">{[1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-ochre" : "bg-stoneLine"}`} />)}</div>
      <AnimatePresence mode="wait">
        {step === 1 && <Step n={1} total={5} title={t("ob_name_title")}>
          <input autoFocus value={p.name} onChange={(e) => set("name", e.target.value)} onKeyDown={(e) => e.key === "Enter" && can && next()} placeholder={t("ob_name_placeholder")} maxLength={30}
            className="w-full rounded-2xl border border-stoneLine bg-sand/40 px-4 py-3 text-lg" data-testid="ob-name-input" />
        </Step>}
        {step === 2 && <Step n={2} total={5} title={t("ob_origin_title").replace("{name}", p.name.trim())}>
          <select value={p.origin} onChange={(e) => set("origin", e.target.value)} className="w-full rounded-2xl border border-stoneLine bg-sand/40 px-4 py-3 text-base" data-testid="ob-origin-select">
            {origins.map((o) => <option key={o.code} value={o.code}>{o.label} · {o.currency}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">{t("ob_origin_hint")}</p>
        </Step>}
        {step === 3 && <Step n={3} total={5} title={t("ob_dest_title")}>
          <LocateButton foundKey="loc_found_dest" countries={countries} onPick={(c) => { set("dest", c); trackEvent("locate_pick", { code: c, where: "onboarding" }); }} />
          <DestGrid countries={countries} lang={lang} value={p.dest} onPick={(c) => set("dest", c)} />
        </Step>}
        {step === 4 && <Step n={4} total={5} title={t("ob_days_title")}>
          <div className="grid grid-cols-2 gap-3">
            {[3, 5].map((n) => (
              <button key={n} type="button" onClick={() => set("days", n)} data-testid={`ob-days-${n}`}
                className={`rounded-2xl border p-4 text-start transition-colors ${p.days === n ? "border-ochre bg-ochre/10" : "border-stoneLine hover:border-ochre/50"}`}>
                <span className="block font-display font-bold text-3xl text-ink">{n}</span>
                <span className="block text-sm text-ink/80">{t(n === 3 ? "ob_days_short" : "ob_days_medium")}</span>
              </button>
            ))}
          </div>
        </Step>}
        {step === 5 && <Step n={5} total={5} title={t("ob_interests_title")}>
          <div className="grid grid-cols-3 gap-2">
            {INTERESTS.map((i) => { const Icon = ICON[i]; const on = p.interests.includes(i); return (
              <button key={i} type="button" onClick={() => toggleInterest(i)} data-testid={`ob-interest-${i}`}
                className={`relative rounded-2xl border p-3 flex flex-col items-center gap-2 text-center transition-colors ${on ? "border-ochre bg-ochre/10" : "border-stoneLine hover:border-ochre/50"}`}>
                {on && <Check className="absolute top-2 end-2 w-3.5 h-3.5 text-ochre" />}
                <Icon className="w-7 h-7 text-ochre" /><span className="text-xs font-semibold text-ink leading-tight">{t(`interest_${i}`)}</span>
              </button>); })}
          </div>
        </Step>}
      </AnimatePresence>
      <div className="flex items-center justify-between mt-6">
        <button type="button" onClick={() => (step > 1 ? setStep(step - 1) : onCancel?.())} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-ink" data-testid="ob-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {step > 1 ? t("back") : t("ob_skip")}
        </button>
        <button type="button" disabled={!can} onClick={next} className="inline-flex items-center gap-2 rounded-full bg-ink text-sand px-6 py-2.5 font-semibold hover:bg-ochre transition-colors disabled:opacity-40" data-testid="ob-next">
          {step === 5 ? t("ob_create") : t("ob_next")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
        </button>
      </div>
    </div>
  );
}
